import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../components/layout';
import RAGForm from '../../../components/rag/RAGForm.tsx';
import KnowledgeBaseSelectionModal from '../../../components/modals/rag/KnowledgeBaseSelectionModal';
import { API_ENDPOINTS } from '../../../config/api.config.ts';
import {
    User,
    RAGFormData,
    KnowledgeBase
} from '../../../types/knowledge-base.ts';
import { IntegrationConfig } from '../../../types/integrations';
import { apiService } from '../../../services/apiService';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface RAGApplicationResponse {
    id: string;
    name: string;
    description?: string;
    llm_model: string;
    index_ids: string[];
    knowledge_bases: KnowledgeBase[];
    integrations: IntegrationConfig[];
}

interface KnowledgeBaseResponse {
    indexes: KnowledgeBase[];
}

const Edit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const [user] = useState<User | null>(authUser);
    const modalState = useModal();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);


    const [formData, setFormData] = useState<RAGFormData>({
        id: '',
        name: '',
        description: '',
        llmModel: 'gpt-3.5-turbo',
        indexIds: [],
        selectedKnowledgeBases: [],
        integrations: []
    });

    useEffect(() => {
        const fetchApplication = async () => {
            try {
                const data = await apiService.get<RAGApplicationResponse>(`${API_ENDPOINTS.RAG.GET}/${id}`);

                setFormData({
                    id: data.id,
                    name: data.name,
                    description: data.description || '',
                    llmModel: data.llm_model,
                    indexIds: data.index_ids,
                    selectedKnowledgeBases: data.knowledge_bases,
                    integrations: data.integrations
                });
            } catch (err) {
                console.error('Error fetching application:', err);
                setError(err instanceof Error ? err.message : 'Failed to fetch application details');
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            fetchApplication();
        }
    }, [id]);

    const handleChange = (
        field: keyof RAGFormData,
        value: string | string[] | KnowledgeBase[] | IntegrationConfig[]
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleKnowledgeBaseSelect = async (selectedIds: string[]) => {
        try {
            const data = await apiService.get<KnowledgeBaseResponse>(
                `${API_ENDPOINTS.INDEXES.LIST}?ids=${selectedIds.join(',')}`
            );

            setFormData(prev => ({
                ...prev,
                indexIds: selectedIds,
                selectedKnowledgeBases: data.indexes
            }));
        } catch (error) {
            console.error('Error fetching knowledge base details:', error);
            setError('Failed to fetch knowledge base details');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const requestBody = {
                name: formData.name,
                description: formData.description,
                llmModel: formData.llmModel,
                indexIds: formData.indexIds,
                integrations: formData.integrations
            };

            const response = await apiService.put<RAGApplicationResponse>(
                `${API_ENDPOINTS.RAG.UPDATE}/${id}`,
                requestBody
            );

            setFormData(prev => ({
                ...prev,
                indexIds: response.index_ids,
                selectedKnowledgeBases: response.knowledge_bases,
                llmModel: response.llm_model
            }));

            modalState.setSuccess('Application updated successfully');
            navigate('/rag-applications');
        } catch (err) {
            console.error('Error updating application:', err);
            setError(err instanceof Error ? err.message : 'Failed to update application');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!id) return;

        try {
            await apiService.delete(`${API_ENDPOINTS.RAG.DELETE}/${id}`);
            modalState.setSuccess('Application deleted successfully');
            navigate('/rag-applications');
        } catch (err) {
            console.error('Error deleting application:', err);
            setError(err instanceof Error ? err.message : 'Failed to delete application');
        }
    };

    if (!user) return null;

    return (
        <>
            <div className="page-container">
                <div className="content-header">
                    <h5 className="text-lg font-semibold mb-3">Edit RAG Application</h5>
                    {isLoading ? (
                        <ContentSkeleton />
                    ) : (
                    <RAGForm
                        formData={formData}
                        onChange={handleChange}
                        onSubmit={handleSubmit}
                        onDelete={handleDelete}
                        error={error}
                        isLoading={isLoading}
                        mode="edit"
                    />
                    )}
                </div>
            </div>

            <KnowledgeBaseSelectionModal
                isOpen={showKnowledgeBaseModal}
                onClose={() => setShowKnowledgeBaseModal(false)}
                onSelect={handleKnowledgeBaseSelect}
                selectedKnowledgeBases={formData.indexIds}
            />
        </>
    );
};

export default Edit;